import React, { useState } from "react";
import { GetCommanWay, UpdateCommanWay, PostFormData } from "API/commonApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment";
import Swal from "sweetalert2";
import UseToast from "utils/AfToast";
import Select from "react-select";
import Spinner from "utils/Spinner";
import { Button } from "@heroui/react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, PencilIcon, CheckCircleIcon } from "@heroicons/react/24/outline";

const statusColors: Record<string, string> = {
  approved: "bg-green-100 text-green-800",
  pending: "bg-yellow-100 text-yellow-800",
  verified: "bg-blue-100 text-blue-800",
  disputed: "bg-orange-100 text-orange-800",
  paid: "bg-emerald-100 text-emerald-800",
};

const cnDnStatusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  accepted: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
};

const VendorInvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Edit mode for invoice details
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState<any>({});

  // CN/DN form state
  const [showCnDn, setShowCnDn] = useState(false);
  const [cnDnNo, setCnDnNo] = useState("");
  const [cnDnType, setCnDnType] = useState<any>({ value: "CN", label: "Credit Note" });
  const [cnDnAmount, setCnDnAmount] = useState("");
  const [cnDnRemark, setCnDnRemark] = useState("");
  const [cnDnFileUrl, setCnDnFileUrl] = useState("");
  const [cnDnDate, setCnDnDate] = useState("");
  const [cnDnStatus, setCnDnStatus] = useState<any>({ value: "pending", label: "Pending" });

  // Fetch invoice detail
  const {
    data: invoiceResp,
    isLoading,
    refetch,
  } = useQuery(["vendorInvoiceDetail", id], () => GetCommanWay(`/admin/vendor-invoice/${id}`, {}, {}), {
    enabled: !!id,
    onSuccess: (res: any) => {
      if (res?.status === "SUCCESS" && res?.data) {
        const d = res.data;
        setEditData({
          invoiceNo: d.invoiceNo || "",
          invoiceDate: d.invoiceDate ? moment(d.invoiceDate, "DD-MM-YYYY HH:mm:ss").format("YYYY-MM-DD") : "",
          periodFrom: d.periodFrom ? moment(d.periodFrom, "DD-MM-YYYY HH:mm:ss").format("YYYY-MM-DD") : "",
          periodTo: d.periodTo ? moment(d.periodTo, "DD-MM-YYYY HH:mm:ss").format("YYYY-MM-DD") : "",
          totalAmount: d.totalAmount || "",
          verifiedAmount: d.verifiedAmount || "",
          remark: d.remark || "",
        });
        // Populate CN/DN if exists
        if (d.cnDnNo) {
          setCnDnNo(d.cnDnNo);
          setCnDnType({ value: d.cnDnType || "CN", label: d.cnDnType === "DN" ? "Debit Note" : "Credit Note" });
          setCnDnAmount(d.cnDnAmount?.toString() || "");
          setCnDnRemark(d.cnDnRemark || "");
          setCnDnFileUrl(d.cnDnFile || "");
          setCnDnDate(d.cnDnDate ? moment(d.cnDnDate, "DD-MM-YYYY HH:mm:ss").format("YYYY-MM-DD") : "");
          setCnDnStatus({
            value: d.cnDnStatus || "pending",
            label: (d.cnDnStatus || "pending").charAt(0).toUpperCase() + (d.cnDnStatus || "pending").slice(1),
          });
        }
      }
    },
  });

  const invoice = invoiceResp?.status === "SUCCESS" ? invoiceResp?.data : null;
  const linkedPickups = invoice?.pickups || [];
  const linkedAwbs = invoice?.awbdata || [];

  // Update invoice mutation
  const { mutate: updateInvoice, isLoading: updating } = useMutation(
    (data: any) => UpdateCommanWay({ url: `/admin/vendor-invoice/update/${id}`, data }),
    {
      onSuccess: (res: any) => {
        if (res?.status === "SUCCESS") {
          UseToast("Invoice updated", "success");
          setEditMode(false);
          refetch();
        } else {
          UseToast(res?.message || "Update failed", "error");
        }
      },
      onError: (err: any) => UseToast(typeof err === "string" ? err : err?.message || "Update failed", "error"),
    }
  );

  // CN/DN mutation
  const { mutate: saveCnDn, isLoading: savingCnDn } = useMutation(
    (data: any) => UpdateCommanWay({ url: `/admin/vendor-invoice/cn-dn/${id}`, data }),
    {
      onSuccess: (res: any) => {
        if (res?.status === "SUCCESS") {
          UseToast("CN/DN saved", "success");
          setShowCnDn(false);
          refetch();
        } else {
          UseToast(res?.message || "Failed to save CN/DN", "error");
        }
      },
      onError: (err: any) => UseToast(typeof err === "string" ? err : err?.message || "Failed to save CN/DN", "error"),
    }
  );

  // Upload CN/DN file
  const { mutate: uploadCnDnFile, isLoading: uploadingCnDn } = useMutation(
    (formData: FormData) => PostFormData("/admin/upload-to-azure", formData),
    {
      onSuccess: (res: any) => {
        if (res?.status === "SUCCESS") {
          setCnDnFileUrl(res?.data?.uploadSuccess?.[0]?.path || "");
          UseToast("File uploaded", "success");
        } else {
          UseToast(res?.message || "Upload failed", "error");
        }
      },
    }
  );

  const handleCnDnFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) {
      const fd = new FormData();
      fd.append("files", f);
      fd.append("folderName", "vendor_cn_dn");
      uploadCnDnFile(fd);
    }
  };

  const handleSaveEdit = () => {
    updateInvoice({
      invoiceNo: editData.invoiceNo,
      invoiceDate: editData.invoiceDate,
      periodFrom: editData.periodFrom,
      periodTo: editData.periodTo,
      totalAmount: editData.totalAmount ? parseFloat(editData.totalAmount) : undefined,
      verifiedAmount: editData.verifiedAmount ? parseFloat(editData.verifiedAmount) : undefined,
      remark: editData.remark,
    });
  };

  const handleSaveCnDn = () => {
    if (!cnDnNo.trim()) {
      UseToast("Please enter CN/DN number", "error");
      return;
    }
    saveCnDn({
      cnDnNo: cnDnNo.trim(),
      cnDnType: cnDnType?.value || "CN",
      cnDnAmount: cnDnAmount ? parseFloat(cnDnAmount) : 0,
      cnDnRemark: cnDnRemark.trim(),
      cnDnFile: cnDnFileUrl || undefined,
      cnDnDate: cnDnDate || undefined,
      cnDnStatus: cnDnStatus?.value || "pending",
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="p-4 text-center">
        <p className="text-gray-500">Invoice not found</p>
        <Button variant="bordered" className="mt-3" onPress={() => navigate("/billing/vendor-invoice")}>
          Back to List
        </Button>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button onClick={() => navigate("/billing/vendor-invoice")} className="text-gray-500 hover:text-gray-700">
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-semibold">Invoice: {invoice.invoiceNo || `#${invoice.id}`}</h1>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[invoice.status] || "bg-gray-100"}`}>
          {invoice.status}
        </span>
      </div>

      {/* Invoice Info Card */}
      <div className="bg-white border rounded-lg p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-md font-medium">Invoice Details</h2>
          <Button size="sm" variant="bordered" onPress={() => setEditMode(!editMode)} startContent={<PencilIcon className="w-3 h-3" />}>
            {editMode ? "Cancel" : "Edit"}
          </Button>
        </div>

        {editMode ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Invoice No</label>
                <input
                  type="text"
                  value={editData.invoiceNo}
                  onChange={(e) => setEditData({ ...editData, invoiceNo: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Invoice Date</label>
                <input
                  type="date"
                  value={editData.invoiceDate}
                  onChange={(e) => setEditData({ ...editData, invoiceDate: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Period From</label>
                <input
                  type="date"
                  value={editData.periodFrom}
                  onChange={(e) => setEditData({ ...editData, periodFrom: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Period To</label>
                <input
                  type="date"
                  value={editData.periodTo}
                  onChange={(e) => setEditData({ ...editData, periodTo: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Total Amount</label>
                <input
                  type="number"
                  value={editData.totalAmount}
                  onChange={(e) => setEditData({ ...editData, totalAmount: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Verified Amount</label>
                <input
                  type="number"
                  value={editData.verifiedAmount}
                  onChange={(e) => setEditData({ ...editData, verifiedAmount: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Remark</label>
              <textarea
                value={editData.remark}
                onChange={(e) => setEditData({ ...editData, remark: e.target.value })}
                className="w-full border rounded px-3 py-2 text-sm"
                rows={2}
              />
            </div>
            <div className="flex justify-end">
              <Button color="primary" size="sm" onPress={handleSaveEdit} isLoading={updating}>
                Save Changes
              </Button>
            </div>
          </>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-y-3 gap-x-6 text-sm">
            <div>
              <span className="text-gray-500">Vendor:</span>{" "}
              <span className="font-medium">{invoice.vendor?.vendor_name || invoice.vendor?.companyName || invoice.vendorId}</span>
            </div>
            <div>
              <span className="text-gray-500">Invoice Date:</span>{" "}
              <span className="font-medium">
                {invoice.invoiceDate ? moment(invoice.invoiceDate, "DD-MM-YYYY HH:mm:ss").format("DD MMM YYYY") : "-"}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Period:</span>{" "}
              <span className="font-medium">
                {invoice.periodFrom
                  ? `${moment(invoice.periodFrom, "DD-MM-YYYY HH:mm:ss").format("DD MMM")} - ${moment(invoice.periodTo, "DD-MM-YYYY HH:mm:ss").format("DD MMM YYYY")}`
                  : "-"}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Total Amount:</span>{" "}
              <span className="font-medium">₹{invoice.totalAmount?.toLocaleString("en-IN") || "0"}</span>
            </div>
            <div>
              <span className="text-gray-500">Verified Amount:</span>{" "}
              <span className="font-medium">₹{invoice.verifiedAmount?.toLocaleString("en-IN") || "0"}</span>
            </div>
            <div>
              <span className="text-gray-500">Verified By:</span> <span className="font-medium">{invoice.verifiedByUser?.name || invoice.verifiedBy || "-"}</span>
            </div>
            {invoice.remark && (
              <div className="col-span-2 md:col-span-3">
                <span className="text-gray-500">Remark:</span> <span>{invoice.remark}</span>
              </div>
            )}
            {invoice.invoiceFile && (
              <div className="col-span-2 md:col-span-3">
                <span className="text-gray-500">Invoice File:</span>{" "}
                <a href={invoice.invoiceFile} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  View File
                </a>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Linked Pickups */}
      {linkedPickups.length > 0 && (
        <div className="bg-white border rounded-lg p-5 mb-5">
          <h2 className="text-md font-medium mb-3">Linked Pickups ({linkedPickups.length})</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {["#", "Pickup Code", "Customer", "Status", "Vendor Remark"].map((h) => (
                    <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {linkedPickups.map((p: any, i: number) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2">{i + 1}</td>
                    <td className="px-3 py-2 font-mono font-medium">{p.pickupCode || p.refCode || p.id}</td>
                    <td className="px-3 py-2">{p.customer?.name || p.customerName || "-"}</td>
                    <td className="px-3 py-2">{p.status || "-"}</td>
                    <td className="px-3 py-2">{p.vendorRemark || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Linked AWBs */}
      {linkedAwbs.length > 0 && (
        <div className="bg-white border rounded-lg p-5 mb-5">
          <h2 className="text-md font-medium mb-3">Linked AWBs ({linkedAwbs.length})</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {["#", "AWB No", "Customer", "Status", "Vendor Remark"].map((h) => (
                    <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {linkedAwbs.map((a: any, i: number) => (
                  <tr key={a.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2">{i + 1}</td>
                    <td className="px-3 py-2 font-mono font-medium">{a.awbNo || a.refCode || a.id}</td>
                    <td className="px-3 py-2">{a.customer?.name || a.customerName || "-"}</td>
                    <td className="px-3 py-2">{a.status || "-"}</td>
                    <td className="px-3 py-2">{a.vendorRemark || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* CN/DN Section */}
      <div className="bg-white border rounded-lg p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-md font-medium">Credit / Debit Note</h2>
          <Button size="sm" variant="bordered" onPress={() => setShowCnDn(!showCnDn)}>
            {invoice.cnDnNo ? "Edit CN/DN" : "Add CN/DN"}
          </Button>
        </div>

        {/* Existing CN/DN display */}
        {invoice.cnDnNo && !showCnDn && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-y-3 gap-x-6 text-sm">
            <div>
              <span className="text-gray-500">No:</span> <span className="font-medium">{invoice.cnDnNo}</span>
            </div>
            <div>
              <span className="text-gray-500">Type:</span> <span className="font-medium">{invoice.cnDnType === "DN" ? "Debit Note" : "Credit Note"}</span>
            </div>
            <div>
              <span className="text-gray-500">Amount:</span> <span className="font-medium">₹{invoice.cnDnAmount?.toLocaleString("en-IN") || "0"}</span>
            </div>
            <div>
              <span className="text-gray-500">Status:</span>{" "}
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cnDnStatusColors[invoice.cnDnStatus] || "bg-gray-100"}`}>
                {invoice.cnDnStatus}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Date:</span>{" "}
              <span>{invoice.cnDnDate ? moment(invoice.cnDnDate, "DD-MM-YYYY HH:mm:ss").format("DD MMM YYYY") : "-"}</span>
            </div>
            {invoice.cnDnRemark && (
              <div className="col-span-2 md:col-span-3">
                <span className="text-gray-500">Remark:</span> <span>{invoice.cnDnRemark}</span>
              </div>
            )}
            {invoice.cnDnFile && (
              <div>
                <a href={invoice.cnDnFile} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                  View CN/DN File
                </a>
              </div>
            )}
          </div>
        )}

        {!invoice.cnDnNo && !showCnDn && <p className="text-sm text-gray-400">No CN/DN added yet</p>}

        {/* CN/DN form */}
        {showCnDn && (
          <div className="border-t pt-4 mt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  CN/DN No <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={cnDnNo}
                  onChange={(e) => setCnDnNo(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm"
                  placeholder="e.g. CN-2026-005"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Type</label>
                <Select
                  options={[
                    { value: "CN", label: "Credit Note" },
                    { value: "DN", label: "Debit Note" },
                  ]}
                  value={cnDnType}
                  onChange={(val: any) => setCnDnType(val)}
                  menuPortalTarget={document.body}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Amount</label>
                <input
                  type="number"
                  value={cnDnAmount}
                  onChange={(e) => setCnDnAmount(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Date</label>
                <input
                  type="date"
                  value={cnDnDate}
                  onChange={(e) => setCnDnDate(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <Select
                  options={[
                    { value: "pending", label: "Pending" },
                    { value: "accepted", label: "Accepted" },
                    { value: "rejected", label: "Rejected" },
                  ]}
                  value={cnDnStatus}
                  onChange={(val: any) => setCnDnStatus(val)}
                  menuPortalTarget={document.body}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">CN/DN File</label>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    onChange={handleCnDnFileUpload}
                    className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-blue-50 file:text-blue-700"
                  />
                  {uploadingCnDn && <Spinner />}
                  {cnDnFileUrl && <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />}
                </div>
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Remark</label>
              <textarea
                value={cnDnRemark}
                onChange={(e) => setCnDnRemark(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm"
                rows={2}
                placeholder="Reason for CN/DN..."
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="bordered" onPress={() => setShowCnDn(false)}>
                Cancel
              </Button>
              <Button size="sm" color="primary" onPress={handleSaveCnDn} isLoading={savingCnDn}>
                Save CN/DN
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorInvoiceDetail;
